/**
 * src/types/charts/waterfall.chart.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Waterfall / bridge chart — shows cumulative effect of sequential values.
 * Positive values create upward segments; negative values create downward
 * segments. `isTotal: true` anchors the bar to zero (subtotal / grand total).
 *
 * FUTURE: renderer not yet implemented — types are production-ready.
 */

import type { HexColor } from '../core/primitives';
import type { ChartDataPoint } from '../core/interfaces.shared';
import {
  makeTitle, makeSubtitle,
  DEFAULT_BACKGROUND, DEFAULT_AXES, DEFAULT_LEGEND, DEFAULT_DATA_LABELS,
} from '../core/defaults.shared';
import type { ChartConfigBase } from '../core/chart-config.base';

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION STYLES
// ─────────────────────────────────────────────────────────────────────────────

export type WaterfallAnimationStyle = 'classic' | 'sequential';

export const WATERFALL_ANIMATION_STYLES: WaterfallAnimationStyle[] = ['classic', 'sequential'];

export const WATERFALL_ELEMENT_PANEL_STYLES: WaterfallAnimationStyle[] = [];


// ─────────────────────────────────────────────────────────────────────────────
// DATA POINT
// ─────────────────────────────────────────────────────────────────────────────

export interface WaterfallDataPoint extends ChartDataPoint {
  chartType: 'waterfall';
  value:     number;     // positive = increase, negative = decrease
  isTotal?:  boolean;    // true = renders as a totals bar anchored at zero
}


// ─────────────────────────────────────────────────────────────────────────────
// SPACING CONFIG
// ─────────────────────────────────────────────────────────────────────────────

export interface WaterfallSpacingConfig {
  chartType:      'waterfall';
  barWidth:       number;     // px. Range: 10–400. Default: 180.
  barSpacing:     number;     // gap between bars. Range: 0–300. Default: 60.
  positiveColor:  HexColor;   // color for increase bars. Default: '#22c55e'.
  negativeColor:  HexColor;   // color for decrease bars. Default: '#ef4444'.
  totalColor:     HexColor;   // color for total bars. Default: '#6366f1'.
}


// ─────────────────────────────────────────────────────────────────────────────
// DEFAULTS
// ─────────────────────────────────────────────────────────────────────────────

export const WATERFALL_DEFAULTS: ChartConfigBase<WaterfallSpacingConfig> = {
  chartType: 'waterfall',
  content: {
    titles:    [makeTitle('Cash Flow Analysis')],
    subtitles: [makeSubtitle('Monthly Bridge')],
    infoBoxes: [],
  },
  animation:  { duration: 6, style: 'sequential', textSpeed: 1 },
  background: { ...DEFAULT_BACKGROUND },
  layout: {
    chartPosition: { x: 0, y: 0 },
    chartMargin:   { top: 200, right: 80, bottom: 120, left: 100 },
  },
  axes:       { ...DEFAULT_AXES },
  legend:     { ...DEFAULT_LEGEND },
  dataLabels: { ...DEFAULT_DATA_LABELS },
  spacing: {
    chartType:     'waterfall',
    barWidth:      180,
    barSpacing:    60,
    positiveColor: '#22c55e',
    negativeColor: '#ef4444',
    totalColor:    '#6366f1',
  },
  export: { quality: '1080p', isLooping: false, canvasFormat: '16:9' },
};
